import React, { useMemo } from 'react';
import { T, tone, fmtDay, fmtShort, todayIso, isWeekend } from './bookingsTheme';
import Avatar from '../shared/Avatar';

// ─── Week planner grid ───────────────────────────────────────────────────────
//
// The view the module was missing. Previously you picked one day at a time from
// a strip and clicked through three tabs to see the answer, so setting a week
// took five separate passes. Here a week is one screen: people down the side,
// days across the top, one resolved status per cell.
//
// The server resolves each cell (leave, stated whereabouts, van, desk) and
// hands back a status code with its label, icon and tone, so this component
// renders whatever vocabulary the office has configured and never hardcodes a
// list of statuses.

function Cell({ w, isToday, weekend, onClick, clickable }) {
  const base = 'w-full h-full min-h-[40px] px-1.5 py-1 border-r border-b last:border-r-0 text-left transition-colors';

  if (!w) {
    return (
      <button
        type="button"
        disabled={!clickable}
        onClick={onClick}
        className={`${base} ${weekend ? 'bg-gray-50/70' : 'bg-white'} border-gray-200
          ${clickable ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-default'}
          ${isToday ? 'ring-1 ring-inset ring-blue-400' : ''}`}
      >
        <span className={`${T.meta} text-gray-300`}>{clickable ? '+' : ''}</span>
      </button>
    );
  }

  // A split day is drawn as its two halves, each in its own colour, because a
  // morning on leave and an afternoon at home are two different answers and
  // showing one as the whole day is how half days went unread.
  if (w.halves) {
    const half = (h, name) => {
      const hc = h ? tone(h.tone) : null;
      return (
        <div className={`flex-1 flex items-center gap-1 px-1.5 min-h-[20px] ${hc ? `${hc.bg} ${hc.text}` : 'bg-white text-gray-300'}`}>
          <span className={`${T.meta} font-bold opacity-60`}>{name}</span>
          {h?.icon && <span aria-hidden="true">{h.icon}</span>}
          <span className={`${T.meta} font-semibold truncate`}>{h ? (h.short_label || h.label) : '—'}</span>
        </div>
      );
    };
    const describe = (h, name) => h && `${name} ${[h.label, h.detail].filter(Boolean).join(' · ')}`;
    // An extra already told as one of the halves (the desk behind the PM
    // office, say) would only repeat itself in the tooltip.
    const parts = [describe(w.halves.am, 'AM'), describe(w.halves.pm, 'PM')].filter(Boolean);
    const extras = (w.extras || []).filter(e => !parts.some(p => p.includes(e)));
    return (
      <button
        type="button"
        disabled={!clickable}
        onClick={onClick}
        title={[...parts, ...extras].join(' · ')}
        className={`w-full h-full min-h-[40px] border-r border-b last:border-r-0 text-left transition-colors
          border-gray-200 flex flex-col items-stretch justify-stretch p-0
          ${clickable ? 'hover:brightness-95 cursor-pointer' : 'cursor-default'}
          ${isToday ? 'ring-1 ring-inset ring-blue-400' : ''}`}
      >
        {half(w.halves.am, 'AM')}
        {half(w.halves.pm, 'PM')}
      </button>
    );
  }

  const c = tone(w.tone);
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onClick}
      title={[w.label, w.detail, w.slot, ...(w.extras || [])].filter(Boolean).join(' · ')}
      className={`${base} ${c.bg} ${c.text} border-gray-200
        ${clickable ? 'hover:brightness-95 cursor-pointer' : 'cursor-default'}
        ${isToday ? 'ring-1 ring-inset ring-blue-400' : ''}`}
    >
      <div className="flex items-center gap-1 leading-tight">
        {w.icon && <span aria-hidden="true">{w.icon}</span>}
        <span className={`${T.meta} font-semibold truncate`}>{w.short_label || w.label}</span>
      </div>
      {/* The seat is the detail people actually scan for, so it gets its own
          line rather than being folded into a tooltip. */}
      {w.detail && <div className={`${T.meta} opacity-80 truncate`}>{w.detail}</div>}
      {w.slot && <div className={`${T.meta} opacity-70`}>{w.slot}</div>}
    </button>
  );
}

export default function WeekPlanner({
  dates = [],
  people = [],
  days = {},           // { 'YYYY-MM-DD': { personId: whereabouts } }
  currentUserId,
  onCellClick,         // (personId, date) => void
  canEditOthers = false,
  emptyMessage = 'Nobody to show',
}) {
  const today = todayIso();

  // You first, then everyone else. Scanning your own week is the common case
  // and it should never mean hunting for your row.
  const ordered = useMemo(() => {
    const me = people.filter(p => p.id === currentUserId);
    const rest = people.filter(p => p.id !== currentUserId);
    return [...me, ...rest];
  }, [people, currentUserId]);

  // Per-day totals, so "which day is the office busy?" is answerable from the
  // same grid rather than a separate report.
  const inCounts = useMemo(() => {
    const out = {};
    for (const d of dates) {
      out[d] = Object.values(days[d] || {}).filter(w => w?.code === 'office').length;
    }
    return out;
  }, [dates, days]);

  if (!ordered.length) {
    return <div className={`${T.body} text-gray-500 bg-white border border-gray-200 rounded-lg p-8 text-center`}>{emptyMessage}</div>;
  }

  const gridCols = { gridTemplateColumns: `minmax(140px, 1.4fr) repeat(${dates.length}, minmax(88px, 1fr))` };

  return (
    // The grid is the one thing here that legitimately needs to scroll
    // sideways on a narrow screen; the page around it must not.
    <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
      <div className="min-w-[640px]">
        {/* Header */}
        <div className="grid border-b border-gray-200 bg-gray-50 sticky top-0 z-10" style={gridCols}>
          <div className={`${T.label} text-gray-500 px-3 py-2`}>Person</div>
          {dates.map(d => (
            <div
              key={d}
              className={`px-1.5 py-2 border-l border-gray-200 text-center ${isWeekend(d) ? 'bg-gray-100' : ''}`}
            >
              <div className={`${T.label} ${d === today ? 'text-blue-600' : 'text-gray-500'}`}>{fmtDay(d)}</div>
              <div className={`${T.body} font-semibold ${d === today ? 'text-blue-700' : 'text-gray-700'}`}>{fmtShort(d)}</div>
              <div className={`${T.meta} text-gray-400`}>{inCounts[d] || 0} in</div>
            </div>
          ))}
        </div>

        {/* Rows */}
        {ordered.map(p => {
          const isMe = p.id === currentUserId;
          return (
            <div key={p.id} className="grid border-b border-gray-100 last:border-b-0" style={gridCols}>
              <div className={`px-3 py-2 flex items-center gap-2 ${isMe ? 'bg-blue-50/60' : 'bg-white'}`}>
                <Avatar as="span" personId={p.id}
                  className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                  style={{ background: p.avatar_color || '#64748b' }}
                >
                  {(p.name || '?').split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase()}
                </Avatar>
                <span className={`${T.body} font-medium text-gray-800 truncate`}>
                  {isMe ? 'You' : p.name}
                </span>
              </div>
              {dates.map(d => (
                <Cell
                  key={d}
                  w={days[d]?.[p.id]}
                  isToday={d === today}
                  weekend={isWeekend(d)}
                  clickable={!!onCellClick && (isMe || canEditOthers)}
                  onClick={() => onCellClick?.(p.id, d)}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
